import { Card, CardContent } from '@crowdfpl/ui-web/card';
import { Skeleton } from '@crowdfpl/ui-web/skeleton';
import { ArrowRight, Crown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DuelCard } from '@/components/duel-card';
import { trackUmamiEvent } from '@/helpers/analytics';
import type { UiDuel } from '@/hooks/data/duels';
import { useIsMobile } from '@/hooks/utils/use-mobile';

interface PopularDuelsSectionProps {
  title: string;
  duels: UiDuel[];
  loading: boolean;
  trackEventName: string;
  showPulsingIndicator?: boolean;
  gameweekId?: string;
  onVote: (duelId: string, side: string) => void;
  isAuthenticated: boolean;
  isVotingByDuelId: Record<string, boolean | undefined>;
  isLiveGameweek?: boolean;
  showSolioXpts?: boolean;
}

export function PopularDuelsSection({
  title,
  duels,
  loading,
  trackEventName,
  showPulsingIndicator = false,
  gameweekId,
  onVote,
  isAuthenticated,
  isVotingByDuelId,
  isLiveGameweek = false,
  showSolioXpts = false,
}: PopularDuelsSectionProps) {
  const isMobile = useIsMobile();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mt-6 md:mt-4">
        <div className="flex items-center gap-2">
          <h2 className="text-center text-lg font-bold leading-tight text-foreground md:text-left md:text-xl">{title}</h2>
          {showPulsingIndicator && (
            <span className="relative flex h-2 w-2 shrink-0 self-center">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
            </span>
          )}
        </div>
        <Link
          to={gameweekId ? `/duels?gameweek=${gameweekId}` : '/duels'}
          onClick={() => trackUmamiEvent(trackEventName)}
          className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground ${isMobile ? 'h-8 px-3 text-xs' : 'h-10 px-4 text-sm'}`}
        >
          View all
          <ArrowRight className="h-3 w-3 md:h-4 md:w-4" />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {loading ? (
          [0, 1, 2].map((i) => (
            <Card key={`duel-skel-${i}`} className="w-full shadow-sm">
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-4 rounded-full" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-5 w-16" />
                    <Skeleton className="h-5 w-14" />
                  </div>
                </div>
                <div className="w-full h-8 flex">
                  <Skeleton className="h-8 w-1/2 rounded-l" />
                  <Skeleton className="h-8 w-1/2 rounded-r ml-0.5" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg border border-border space-y-3">
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-4 rounded" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-5 w-10" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                    <Skeleton className="h-4 w-20" />
                  </div>
                  <div className="p-3 rounded-lg border border-border space-y-3">
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-4 rounded" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-5 w-10" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                    <Skeleton className="h-4 w-20" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : duels.length === 0 ? (
          <Card className="w-full shadow-sm col-span-1 md:col-span-2 xl:col-span-3">
            <CardContent className="p-6 md:p-8 flex flex-col items-center text-center space-y-3">
              <Crown className="h-10 w-10 md:h-12 md:w-12 text-muted-foreground" />
              <h3 className="text-base md:text-lg font-semibold">No popular duels yet</h3>
              <p className="text-sm text-muted-foreground max-w-xl">
                Be the first to vote and help the crowd pick winners for the current gameweek.
              </p>
              <Link
                to="/duels"
                onClick={() => trackUmamiEvent(trackEventName)}
                className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground ${isMobile ? 'h-9 px-3' : 'h-10 px-4'}`}
              >
                Go to duels
                <ArrowRight className="h-4 w-4" />
              </Link>
            </CardContent>
          </Card>
        ) : (
          duels.map((duel) => {
            return (
              <DuelCard
                key={duel.id}
                duel={duel}
                onVote={(id, side) => onVote(id, side)}
                isAuthenticated={isAuthenticated}
                isVoting={!!isVotingByDuelId[duel.id]}
                state={(duel as unknown as { state?: 'active' | 'locked' | 'resolved' }).state || 'active'}
                selectedPlayerId={(duel as unknown as { selectedPlayerId?: 'player1' | 'player2' | null }).selectedPlayerId || null}
                playerAPoints={duel.player1.gameweek_stats?.total_points}
                playerBPoints={duel.player2.gameweek_stats?.total_points}
                trackSource="home_popular_duels"
                isLiveGameweek={isLiveGameweek}
                solioPredictedA={showSolioXpts ? (duel.player1.solioProjectedPoints ?? undefined) : undefined}
                solioPredictedB={showSolioXpts ? (duel.player2.solioProjectedPoints ?? undefined) : undefined}
                predictionOutcome={duel.votes.prediction_outcome}
                ratingChange={duel.votes.rating_change}
                isDuelOfTheWeek={duel.isDuelOfTheWeek}
                showDOTWBanner={false}
              />
            );
          })
        )}
      </div>
    </div>
  );
}

